import Text from 'components/commons/Text';
import LocalizeInput from 'components/input/LocalizeInput';
import { InnerGroup } from 'components/layouts/CommonForm';
import { InnerSection } from 'components/layouts/CommonForm';
import React, { useEffect } from 'react';
import { Button, Row, useModelStateContext } from 'saladsoft-primitive';
import InputField from 'components/input/InputField';
import LocalizeEditor from 'components/input/LocalizeEditor';
import UploadButton from 'components/input/UploadButton';
import { AdminOptionDto, AdminOptionRequestDto } from 'services/data-contracts';
import ServiceHelper from 'stores/ServiceHelper';
import styled, { AnyStyledComponent } from 'styled-components';
import { toast } from 'react-toastify';
import { useStoreWrapper } from 'stores/StoreHelper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from 'controls/IconButton';

const NewOptionModal = ({ option }: { option?: AdminOptionDto }) => {
  const { uiStore } = useStoreWrapper();
  const { stateModel, setState, setMultipleState, serviceModel, updateServiceModelMultiple } =
    useModelStateContext<AdminOptionDto>();

  const [image, setImage] = React.useState<File | null>(null);

  const deleteImages = async () => {
    if (option?.images && option.images.length > 0) {
      const deletePromises = option.images.map((image) =>
        ServiceHelper?.adminOptionService?.deleteOptionImage(
          option.id as number,
          image.id as number,
        ),
      );
      await Promise.all(deletePromises);
    }
    return;
  };

  const handleClickCreate = () => {
    if (option?.id) {
      ServiceHelper?.adminOptionService
        ?.updateOption(option?.id, serviceModel.current as AdminOptionRequestDto)
        .then(async (res) => {
          const optionId = res.data.id;
          if (optionId && image) {
            await deleteImages();
            ServiceHelper?.adminOptionService
              ?.createOptionImage(optionId, { seq: 1 }, { file: image! })
              .then((res) => {
                closeModal('상품 수정이 완료되었습니다.');
                return;
              })
              .catch((err) => {
                toast.error(err.response.data.message);
              });
          } else {
            closeModal('상품 수정이 완료되었습니다.');
          }
        });
    } else {
      if (stateModel) {
        const newStateModel = { ...stateModel };
        newStateModel.useQty = true;
        newStateModel.isActive = true;
        ServiceHelper?.adminOptionService?.createOption(newStateModel).then((res) => {
          if (res.status === 200) {
            const optionId = res.data?.id;
            if (optionId && image) {
              ServiceHelper?.adminOptionService
                ?.createOptionImage(optionId, { seq: 1 }, { file: image! })
                .then((res) => {
                  closeModal('상품 생성이 완료되었습니다.');
                });
            } else {
              closeModal('상품 생성이 완료되었습니다.');
            }
          } else {
            const newError = res as any;
            toast.error(newError.response.data.errorCode);
          }
        });
      }
    }
  };

  const handleClickDelete = () => {
    if (option?.id) {
      uiStore?.confirm.show({
        title: '상품 삭제',
        message: '상품을 삭제하시겠습니까?',
        onConfirmed: () => {
          ServiceHelper?.adminOptionService?.deleteOption(option?.id as number).then((res) => {
            closeModal('상품 삭제가 완료되었습니다.');
          });
        },
      });
    }
  };

  const closeModal = (title: string) => {
    toast.success(title);
    uiStore?.modal.confirm();
  };

  useEffect(() => {
    if (option) {
      setMultipleState(option);
    }
  }, [option]);

  return (
    <div>
      <InnerSection style={{ minHeight: '500px' }}>
        <InnerGroup>
          <div>
            <Text type="H3" style={{ marginBottom: 10 }}>
              상품명 등록
            </Text>
            <Text type="H4" buttonRole="DESCRIPTION">
              옵션 상품의 이름을 입력하여 주세요.
            </Text>
            <div style={{ marginTop: 50 }}>
              <UploadButton
                onChange={(file) => {
                  setImage(file);
                }}
              >
                사진등록
              </UploadButton>
            </div>
          </div>
          <div>
            <LocalizeInput
              defaultValue={option?.localizedName}
              onChange={(value) => {
                setState('localizedName', value);
              }}
            />
          </div>
        </InnerGroup>
        <InnerGroup style={{ marginTop: 40 }}>
          <div>
            <Text type="H3" style={{ marginBottom: 10 }}>
              상품가격 및 옵션 설정
            </Text>
            <Text type="H4" buttonRole="DESCRIPTION">
              상품의 판매 가격을 입력하여 주세요.
              <br />
              숫자만 입력 가능합니다.
            </Text>
            <Text
              type="H5"
              buttonRole="DESCRIPTION"
              style={{ fontSize: '12px', color: 'var(--point-color)' }}
            >
              * 모든 입력값은 필수입니다.
            </Text>
          </div>
          <div>
            <InputField
              type="number"
              defaultValue={option?.price?.toString() ?? ''}
              onChange={(value) => {
                setState('price', Number(value));
              }}
            />
            <Row marginTop={10} justifyContent="flex-start">
              <Checkbox
                checked={stateModel?.useQty ?? false}
                onChange={(e) => {
                  setState('useQty', e.target.checked);
                }}
              />
              <Text type="H4" style={{ marginLeft: 0 }}>
                수량 사용(수량옵션을 사용할 수 있습니다.)
              </Text>
            </Row>
            <Row marginTop={10} justifyContent="flex-start">
              <Checkbox
                checked={stateModel?.isActive ?? false}
                onChange={(e) => {
                  setState('isActive', e.target.checked);
                }}
              />
              <Text type="H4" style={{ marginLeft: 0 }}>
                활성여부
              </Text>
            </Row>
          </div>
        </InnerGroup>

        <InnerGroup style={{ marginTop: 40 }}>
          <div>
            <Text type="H3" style={{ marginBottom: 10 }}>
              상품정보 등록
            </Text>
            <Text type="H4" buttonRole="DESCRIPTION">
              상품의 상세 내용을 등록 해주세요.
            </Text>
            <Text
              type="H5"
              buttonRole="DESCRIPTION"
              style={{ fontSize: '12px', color: 'var(--point-color)' }}
            >
              * 모든 입력값은 필수입니다.
            </Text>
          </div>
          <div>
            <LocalizeEditor
              defaultValue={option?.localizedDesc}
              onChange={(value) => {
                setState('localizedDesc', value);
              }}
            />
          </div>
        </InnerGroup>
        <ButtonGroupWrapper>
          <Row width="100%" justifyContent="space-between" alignItems="center">
            <Row gap={10} marginTop={40} justifyContent="flex-start">
              <Button
                type="outlined"
                onClick={() => {
                  uiStore?.modal.close();
                }}
              >
                생성 취소
              </Button>
              <Button onClick={handleClickCreate}>
                {option?.id ? '상품 수정하기' : '상품 생성하기'}
              </Button>
            </Row>
            <IconButton
              type="delete"
              onClick={() => {
                handleClickDelete();
              }}
            />
          </Row>
        </ButtonGroupWrapper>
      </InnerSection>
    </div>
  );
};

const ButtonGroupWrapper = styled.div``;

export default NewOptionModal;
